import { SearchField } from '@/components/search-field';
import {
  Button,
  ListPage,
  SegmentedControl,
  SegmentedControlItem,
  Spinner,
} from '@mgrvtechgroup/admin-ds';
import { useId } from 'react';
import {
  toApplicationSegment,
  type NativePropertyListQuery,
  type NativePropertyListView,
} from './native-property-list-query';

/**
 * 지점 목록 툴바 — 맨 앞의 보기 전환(운영 지점·삭제된 지점), 검색, 신청 허용 구간, 초기화, 그리고 오른쪽의 재조회 표시.
 * - 보기 전환은 표 자리에 보여 줄 목록을 바꾼다. 검색은 두 보기가 함께 쓰고, 신청 허용은 운영 지점에만 있는 값이라 그 보기에서만 나온다.
 * - 검색 안내는 입력칸 안에 둬서 옆 컨트롤과의 간격이 벌어지지 않는다.
 * - 신청 허용은 한 번에 고르는 구간(전체·허용·차단)이다. 건수나 결과 수는 붙이지 않는다.
 */
export function NativePropertyListToolbar({
  view,
  onViewChange,
  query,
  filtered,
  refreshing,
  onSearch,
  onApplication,
  onReset,
}: {
  view: NativePropertyListView;
  onViewChange: (view: NativePropertyListView) => void;
  query: NativePropertyListQuery;
  /** 이 보기에서 보이는 조건이 걸려 있는가 — 초기화 버튼을 낸다. */
  filtered: boolean;
  /** 이미 받은 목록을 보여 둔 채 새로 받는 중. */
  refreshing: boolean;
  onSearch: (value: string) => void;
  onApplication: (segment: string) => void;
  onReset: () => void;
}) {
  const labelId = useId();
  return (
    <ListPage.Toolbar
      left={
        // 좁은 폭에서는 컨트롤을 줄바꿈해 글자가 세로로 쪼개지지 않게 한다.
        <div className="flex w-full flex-wrap items-center gap-x-3 gap-y-2">
          <SegmentedControl
            size="sm"
            aria-label="지점 보기"
            value={view}
            onValueChange={(next) => {
              onViewChange(next === 'deleted' ? 'deleted' : 'active');
            }}
          >
            <SegmentedControlItem value="active">운영 지점</SegmentedControlItem>
            <SegmentedControlItem value="deleted">삭제된 지점</SegmentedControlItem>
          </SegmentedControl>
          <SearchField
            hint="inside"
            // 검색은 Enter·지우기에서만 주소에 확정한다(C29). 조건은 이 페이지 안에서만 걸리므로 페이지(cursor)는 그대로 둔다.
            value={query.q}
            onSearch={onSearch}
            placeholder="지점명·코드·ID 검색"
            aria-label="지점 검색"
          />
          {view === 'active' && (
            <div className="flex items-center gap-2">
              <span id={labelId} className="body text-muted-foreground">
                신청 허용
              </span>
              <SegmentedControl
                size="sm"
                aria-labelledby={labelId}
                value={toApplicationSegment(query.application)}
                onValueChange={onApplication}
              >
                <SegmentedControlItem value="all">전체</SegmentedControlItem>
                <SegmentedControlItem value="enabled">허용</SegmentedControlItem>
                <SegmentedControlItem value="disabled">차단</SegmentedControlItem>
              </SegmentedControl>
            </div>
          )}
          {filtered && (
            <Button size="sm" variant="ghost" onClick={onReset}>
              초기화
            </Button>
          )}
        </div>
      }
      right={
        // 알림 영역은 항상 둬서 내용이 바뀔 때 읽히게 한다. 재조회 문구는 화면에는 짧게, 보조 기기에는 전체 문장으로 준다.
        <span className="caption text-muted-foreground flex items-center" aria-live="polite">
          {refreshing && (
            <span className="flex items-center gap-1.5">
              <Spinner className="size-3.5" />
              <span aria-hidden>불러오는 중</span>
              <span className="sr-only">
                {view === 'deleted'
                  ? '삭제된 지점 목록을 새로 불러오고 있습니다.'
                  : '지점 목록을 새로 불러오고 있습니다.'}
              </span>
            </span>
          )}
        </span>
      }
    />
  );
}
